import { SOCIAL_FIELDS, SOCIAL_LABELS, normalizeSocialUrl } from '../../lib/utils/clientSocial';
import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../AppProvider';
import { PanelSection } from '../shared/property-rows';

interface Proposal {
  field: string;
  label: string;
  value: string;
  /** Valore attuale sul cliente ('' = campo vuoto). */
  current: string;
  source: 'sito' | 'VIES';
  checked: boolean;
}

interface Props {
  selectedClient: any;
  onLeadUpdate: (id: string, updates: any) => Promise<boolean>;
}

const FIELD_LABELS: Record<string, string> = {
  ...SOCIAL_LABELS,
  business_name: 'Ragione sociale',
  email: 'Email',
  phone: 'Telefono',
  pec: 'PEC',
  vat_number: 'P.IVA',
  street_address: 'Indirizzo',
  street_number: 'Civico',
  postal_code: 'CAP',
  city: 'Città',
  province: 'Provincia',
  country: 'Paese',
};

const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();
/** Solo cifre, ultime 10: confronto telefoni robusto a prefissi/spazi. */
const phoneKey = (v: unknown) => String(v ?? '').replace(/\D/g, '').slice(-10);

type Status = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

/**
 * "Arricchimento dati" nel pannello lead/cliente: analizza sito web e P.IVA
 * (edge `lead-enrich-preview`, nessuna scrittura) e mostra i dati trovati come
 * PROPOSTE con spunta per campo. Spuntate di default solo quelle che riempiono
 * campi vuoti; le sostituzioni vanno scelte esplicitamente. "Applica" salva
 * solo i campi selezionati; il resto può finire nei commenti a scelta.
 */
export function EnrichmentProposalsSection({ selectedClient, onLeadUpdate }: Props) {
  const { toast } = useApp();
  const [status, setStatus] = useState<Status>('idle');
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [notesBlock, setNotesBlock] = useState<{ lines: string[]; checked: boolean } | null>(null);
  const [viesInvalid, setViesInvalid] = useState(false);
  const [applying, setApplying] = useState(false);

  const website = String(selectedClient?.website ?? '').trim();
  const vatNumber = String(selectedClient?.vat_number ?? '').trim();
  const hasSources = !!website || !!vatNumber;

  const run = async () => {
    setStatus('loading');
    setViesInvalid(false);
    try {
      const body: Record<string, string> = {};
      if (website) body.website = website;
      if (vatNumber) body.vat_number = vatNumber;
      const { data, error } = await supabase.functions.invoke('lead-enrich-preview', { body });
      if (error) { setStatus('error'); return; }

      const next: Proposal[] = [];
      const push = (field: string, value: string | null | undefined, source: 'sito' | 'VIES') => {
        if (!value || !String(value).trim()) return;
        const current = String(selectedClient?.[field] ?? '').trim();
        const same = field === 'phone'
          ? phoneKey(current) === phoneKey(value)
          : norm(current) === norm(value);
        if (current && same) return; // già identico: niente da proporre
        if (next.some((p) => p.field === field)) return; // prima fonte vince
        next.push({
          field,
          label: FIELD_LABELS[field] ?? field,
          value: String(value).trim(),
          current,
          source,
          checked: !current, // riempi i vuoti di default; sostituzioni opt-in
        });
      };

      const vies = data?.vies;
      if (vies) {
        if (vies.valid === false) setViesInvalid(true);
        else {
          push('business_name', vies.business_name, 'VIES');
          push('street_address', vies.street_address, 'VIES');
          push('street_number', vies.street_number, 'VIES');
          push('postal_code', vies.postal_code, 'VIES');
          push('city', vies.city, 'VIES');
          push('province', vies.province, 'VIES');
          push('country', vies.country, 'VIES');
        }
      }

      const wd = data?.website;
      if (wd && !wd.error) {
        push('email', wd.email, 'sito');
        push('phone', wd.phone, 'sito');
        push('pec', wd.pec, 'sito');
        push('vat_number', wd.vat_number, 'sito');
      }

      // Extra senza campo dedicato → proposta unica "note nei commenti"
      const lines: string[] = [];
      if (wd?.description) lines.push(`Descrizione sito: ${wd.description}`);
      for (const field of SOCIAL_FIELDS) {
        try { push(field, normalizeSocialUrl(wd?.social_links?.[field.replace('_url', '')]), 'sito'); } catch { /* Ignore malformed enrichment URLs. */ }
      }
      const socials = Object.entries(wd?.social_links ?? {}).filter(([key]) => !SOCIAL_FIELDS.includes(`${key}_url` as any));
      if (socials.length > 0) lines.push(`Social: ${socials.map(([k, v]) => `${k}: ${v}`).join(' | ')}`);
      const proposedEmail = next.find((p) => p.field === 'email')?.value ?? '';
      const otherEmails = ((wd?.emails as string[] | undefined) ?? [])
        .filter((e) => norm(e) !== norm(proposedEmail) && norm(e) !== norm(selectedClient?.email));
      if (otherEmails.length > 0) lines.push(`Altre email trovate: ${otherEmails.join(', ')}`);
      const proposedPhone = next.find((p) => p.field === 'phone')?.value ?? '';
      const otherPhones = ((wd?.phones as string[] | undefined) ?? [])
        .filter((p) => phoneKey(p) !== phoneKey(proposedPhone) && phoneKey(p) !== phoneKey(selectedClient?.phone));
      if (otherPhones.length > 0) lines.push(`Altri telefoni trovati: ${otherPhones.join(', ')}`);
      // Non riproporre note già presenti nei commenti
      const comments = String(selectedClient?.general_comments ?? '');
      const freshLines = lines.filter((l) => !comments.includes(l));

      setProposals(next);
      setNotesBlock(freshLines.length > 0 ? { lines: freshLines, checked: false } : null);
      setStatus(next.length > 0 || freshLines.length > 0 ? 'ready' : 'empty');
    } catch {
      setStatus('error');
    }
  };

  const toggle = (field: string) =>
    setProposals((prev) => prev.map((p) => (p.field === field ? { ...p, checked: !p.checked } : p)));

  const selectedCount = proposals.filter((p) => p.checked).length + (notesBlock?.checked ? 1 : 0);

  const apply = async () => {
    if (!selectedClient?.id || selectedCount === 0) return;
    setApplying(true);
    try {
      const updates: Record<string, any> = {};
      for (const p of proposals) if (p.checked) updates[p.field] = p.value;
      if (notesBlock?.checked) {
        const block = `\n\n--- Enrichment (${new Date().toLocaleDateString('it-IT')}) ---\n${notesBlock.lines.join('\n')}`;
        updates.general_comments = String(selectedClient?.general_comments ?? '') + block;
      }
      const ok = await onLeadUpdate(selectedClient.id, updates);
      if (ok) {
        toast.success(`${selectedCount} ${selectedCount === 1 ? 'proposta applicata' : 'proposte applicate'}`);
        const remaining = proposals.filter((p) => !p.checked);
        setProposals(remaining);
        if (notesBlock?.checked) setNotesBlock(null);
        if (remaining.length === 0 && !(notesBlock && !notesBlock.checked)) setStatus('idle');
      } else {
        toast.error("Errore nell'applicare le proposte");
      }
    } finally {
      setApplying(false);
    }
  };

  const linkCls = 'text-[12px] text-[#337EA9] dark:text-[#529CCA] hover:underline disabled:opacity-40';

  return (
    <PanelSection
      title="Arricchimento dati"
      summary={status === 'ready' ? `${proposals.length + (notesBlock ? 1 : 0)} proposte` : undefined}
    >
      <div className="px-1.5">
        {!hasSources ? (
          <p className="py-1 text-[12px] text-[#9B9A97] dark:text-gray-500">
            Aggiungi il sito web o la P.IVA per cercare dati da proporre.
          </p>
        ) : status === 'loading' ? (
          <p className="flex items-center gap-2 py-1 text-[12px] text-[#787774] dark:text-gray-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Analisi di {[website && 'sito web', vatNumber && 'P.IVA (VIES)'].filter(Boolean).join(' e ')} in corso…
          </p>
        ) : status === 'idle' ? (
          <button type="button" onClick={run} className="flex items-center gap-1.5 py-1 text-[12.5px] text-[#337EA9] dark:text-[#529CCA] hover:underline">
            <Sparkles className="h-3.5 w-3.5" />
            Analizza {[website && 'sito web', vatNumber && 'P.IVA'].filter(Boolean).join(' e ')}
          </button>
        ) : status === 'error' ? (
          <p className="py-1 text-[12px] text-[#787774] dark:text-gray-400">
            Analisi non riuscita.{' '}
            <button type="button" onClick={run} className={linkCls}>Riprova</button>
          </p>
        ) : status === 'empty' ? (
          <p className="py-1 text-[12px] text-[#787774] dark:text-gray-400">
            Nessun dato nuovo trovato: i campi sono già allineati.{' '}
            <button type="button" onClick={run} className={linkCls}>Riprova</button>
          </p>
        ) : (
          <div>
            {viesInvalid && (
              <p className="mb-1 text-[11px] text-[#B7791F]">P.IVA non valida secondo VIES.</p>
            )}
            {proposals.map((p) => (
              <label key={p.field} className="flex cursor-pointer items-start gap-2 rounded px-1.5 py-1.5 -mx-1.5 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03]">
                <input
                  type="checkbox"
                  checked={p.checked}
                  onChange={() => toggle(p.field)}
                  className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-[#E91E8C]"
                />
                <span className="min-w-0 flex-1">
                  <span className="text-[13px] text-[#787774] dark:text-gray-400">{p.label}</span>{' '}
                  <span className="break-words text-[13px] font-medium text-[#37352F] dark:text-white">{p.value}</span>
                  <span className="block text-[11px] text-[#9B9A97] dark:text-gray-500">
                    {p.current ? (
                      <span className="text-[#B7791F]">sostituisce: {p.current}</span>
                    ) : 'campo vuoto'}
                    {' · '}fonte: {p.source}
                  </span>
                </span>
              </label>
            ))}
            {notesBlock && (
              <label className="flex cursor-pointer items-start gap-2 rounded px-1.5 py-1.5 -mx-1.5 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03]">
                <input
                  type="checkbox"
                  checked={notesBlock.checked}
                  onChange={() => setNotesBlock((n) => (n ? { ...n, checked: !n.checked } : n))}
                  className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-[#E91E8C]"
                />
                <span className="min-w-0 flex-1">
                  <span className="text-[13px] text-[#37352F] dark:text-white">Aggiungi il resto ai Commenti</span>
                  <span className="block truncate text-[11px] text-[#9B9A97] dark:text-gray-500" title={notesBlock.lines.join(' · ')}>
                    {notesBlock.lines.join(' · ')}
                  </span>
                </span>
              </label>
            )}
            <div className="mt-1.5 flex items-center gap-3">
              <button
                type="button"
                onClick={apply}
                disabled={applying || selectedCount === 0}
                className="inline-flex items-center gap-1.5 rounded bg-[#37352F] px-2.5 py-1 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-40 dark:bg-white dark:text-[#191919]"
              >
                {applying && <Loader2 className="h-3 w-3 animate-spin" />}
                Applica {selectedCount > 0 ? selectedCount : ''} selezionat{selectedCount === 1 ? 'a' : 'e'}
              </button>
              <button type="button" onClick={() => { setProposals([]); setNotesBlock(null); setStatus('idle'); }} className={linkCls} disabled={applying}>
                Scarta
              </button>
              <button type="button" onClick={run} className={linkCls} disabled={applying}>
                Riprova
              </button>
            </div>
          </div>
        )}
      </div>
    </PanelSection>
  );
}
